import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { CheckCircle, ArrowRight, ArrowLeft, User, Buildings, MapPin, Sparkle } from '@phosphor-icons/react';
import { TERRITOIRES } from '../constants/countries';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTEURS = [
  'Agriculture / Pêche', 'Agroalimentaire', 'Bâtiment / BTP', 'Commerce', 'Culture / Arts',
  'Éducation / Formation', 'Environnement', 'Industrie', 'Numérique / IT', 'Santé / Social',
  'Services aux entreprises', 'Tourisme / Hôtellerie', 'Transport / Logistique', 'Autre',
];

export default function ProfileCompletionWizard({ open, onOpenChange, onCompleted }) {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    prenom: '', nom: '', telephone: '',
    type_acteur: 'personne_physique',
    territoire: '', secteur: '',
    raison_sociale: '', siret: '',
    description: '',
  });

  React.useEffect(() => {
    if (user?.name) {
      const parts = user.name.trim().split(' ');
      setF((s) => ({ ...s, prenom: s.prenom || parts[0] || '', nom: s.nom || parts.slice(1).join(' ') || '' }));
    }
  }, [user]);

  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const steps = [
    { key: 'identity', title: 'Votre identité', icon: User, desc: 'Vérifions les informations de base.' },
    { key: 'context', title: 'Votre territoire', icon: MapPin, desc: 'Où et dans quel secteur agissez-vous ?' },
    { key: 'structure', title: 'Votre structure', icon: Buildings, desc: 'Personne physique ou morale ?' },
    { key: 'motivation', title: 'Votre projet', icon: Sparkle, desc: 'Un mot sur vos intentions au sein de la coopérative.' },
  ];

  const canGoNext = () => {
    if (step === 0) return f.prenom.trim() && f.nom.trim();
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        prenom: f.prenom.trim() || null,
        nom: f.nom.trim() || null,
        telephone: f.telephone.trim() || null,
        territoire: f.territoire || null,
        secteur: f.secteur || null,
        type_acteur: f.type_acteur || null,
        raison_sociale: f.type_acteur === 'personne_morale' ? (f.raison_sociale.trim() || null) : null,
        siret: f.type_acteur === 'personne_morale' ? (f.siret.replace(/\s/g, '') || null) : null,
        description: f.description.trim() || null,
      };
      await axios.post(`${API}/api/auth/complete-profile`, payload, { withCredentials: true });
      toast.success('Profil enregistré — merci !');
      try { await checkAuth(); } catch {}
      onCompleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const StepIcon = steps[step].icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="profile-wizard">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2D5A4C] to-[#1E4136] text-white flex items-center justify-center">
              <StepIcon size={20} weight="fill" />
            </div>
            <div>
              <DialogTitle className="text-lg" style={{ fontFamily: 'Manrope' }} data-testid="wizard-step-title">
                {steps[step].title}
              </DialogTitle>
              <DialogDescription className="text-xs">{steps[step].desc}</DialogDescription>
            </div>
          </div>
          {/* Progress */}
          <div className="flex gap-1.5 pt-2" data-testid="wizard-progress">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#80C51F]' : 'bg-[#E8E6E1]'}`} />
            ))}
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          {step === 0 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Prénom *</Label>
                  <Input value={f.prenom} onChange={(e) => update('prenom', e.target.value)} data-testid="wizard-prenom" />
                </div>
                <div className="space-y-1.5">
                  <Label>Nom *</Label>
                  <Input value={f.nom} onChange={(e) => update('nom', e.target.value)} data-testid="wizard-nom" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Téléphone</Label>
                <Input value={f.telephone} onChange={(e) => update('telephone', e.target.value)} placeholder="+590 690 00 00 00" data-testid="wizard-telephone" />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="space-y-1.5">
                <Label>Territoire</Label>
                <Select value={f.territoire} onValueChange={(v) => update('territoire', v)}>
                  <SelectTrigger data-testid="wizard-territoire"><SelectValue placeholder="Sélectionner votre territoire" /></SelectTrigger>
                  <SelectContent>
                    {TERRITOIRES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Secteur d'activité</Label>
                <Select value={f.secteur} onValueChange={(v) => update('secteur', v)}>
                  <SelectTrigger data-testid="wizard-secteur"><SelectValue placeholder="Sélectionner un secteur" /></SelectTrigger>
                  <SelectContent>
                    {SECTEURS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'personne_physique', l: 'Personne physique', d: 'Un particulier, artisan, indépendant' },
                  { v: 'personne_morale', l: 'Personne morale', d: 'Une entreprise, association, coopérative' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => update('type_acteur', opt.v)}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${
                      f.type_acteur === opt.v ? 'border-[#2D5A4C] bg-[#2D5A4C]/5' : 'border-[#E8E6E1] hover:border-[#2D5A4C]/40'
                    }`}
                    data-testid={`wizard-type-${opt.v}`}
                  >
                    <p className="text-sm font-bold text-[#1A2421]">{opt.l}</p>
                    <p className="text-[11px] text-[#5E6D66] mt-0.5">{opt.d}</p>
                  </button>
                ))}
              </div>
              {f.type_acteur === 'personne_morale' && (
                <>
                  <div className="space-y-1.5">
                    <Label>Raison sociale</Label>
                    <Input value={f.raison_sociale} onChange={(e) => update('raison_sociale', e.target.value)} placeholder="Ex : Coopérative des Îles SARL" data-testid="wizard-raison-sociale" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>SIRET <span className="text-[10px] text-[#5E6D66] font-normal">(14 chiffres)</span></Label>
                    <Input value={f.siret} onChange={(e) => update('siret', e.target.value)} placeholder="123 456 789 00012" data-testid="wizard-siret" />
                  </div>
                </>
              )}
            </>
          )}

          {step === 3 && (
            <div className="space-y-1.5">
              <Label>Décrivez votre projet ou vos attentes (facultatif)</Label>
              <Textarea
                rows={5}
                value={f.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Ex : Je souhaite proposer mes prestations agroalimentaires via la coopérative..."
                data-testid="wizard-description"
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs text-[#5E6D66] hover:text-[#1A2421] underline underline-offset-4"
            data-testid="wizard-skip"
          >
            Compléter plus tard
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={saving} data-testid="wizard-prev">
                <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Retour
              </Button>
            )}
            {step < steps.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canGoNext()}
                className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white"
                data-testid="wizard-next"
              >
                Suivant <ArrowRight size={14} weight="bold" className="ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} className="bg-[#80C51F] hover:bg-[#6BAB16] text-white" data-testid="wizard-submit">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle size={14} weight="fill" className="mr-1.5" /> Enregistrer</>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
